/**
 * Protected Section Transformer
 *
 * Handles conversion between markdown and ProtectedSectionNode for protected sections.
 * The heading (### Gebreken) remains as a normal H3, only the placeholder (`gebreken_tabel_via_tool`) is replaced.
 */

import type { ElementTransformer } from '@lexical/markdown';
import type { LexicalNode } from 'lexical';
import {
  $createProtectedSectionNode,
  $isProtectedSectionNode,
  ProtectedSectionNode,
} from '../nodes/ProtectedSectionNode';

/**
 * Transformer for protected section placeholders
 * This matches ONLY the placeholder line, not the heading
 */
export const PROTECTED_SECTION_TRANSFORMER: ElementTransformer = {
  dependencies: [ProtectedSectionNode],
  type: 'element',

  /**
   * Export: Convert ProtectedSectionNode to markdown placeholder
   */
  export: (node: LexicalNode) => {
    if (!$isProtectedSectionNode(node)) {
      return null;
    }

    // Only export the placeholder - the heading is a separate H3 element
    return node.getPlaceholder();
  },

  /**
   * Import regex: Match ONLY the placeholder line (inline code with specific text)
   */
  regExp: /^`gebreken_tabel_via_tool`$/,

  /**
   * Replace: Convert placeholder to ProtectedSectionNode
   */
  replace: (parentNode, _children, _match, isImport) => {
    if (!isImport) return;

    const protectedNode = $createProtectedSectionNode('### Gebreken', '`gebreken_tabel_via_tool`');
    parentNode.replace(protectedNode);
  },
};

/**
 * Deprecated: No longer needed with TextMatchTransformer approach
 * The TextMatchTransformer now directly replaces `gebreken` without preprocessing
 */
export function preprocessProtectedSections(markdown: string): {
  processedMarkdown: string;
  protectedSections: Map<string, { heading: string; placeholder: string }>;
} {
  // No preprocessing needed - return markdown as-is
  return {
    processedMarkdown: markdown,
    protectedSections: new Map(),
  };
}
